import {DomainStore} from 'shared/store';
import { makeAutoObservable } from 'mobx';
import {t} from 'shared/core';
import {auth, endpoints, types} from 'main/tools';
import {successAlert} from 'shared/tools';

class SurveyDirectoryState {
  store = new DomainStore();
  history;

  selectedSurvey;
  closeSurveyModalOpen = false;
  relaunchSurveyModalOpen = false;
  reopenSurveyModalOpen = false;
  welcomeEmailPreview;

  constructor() {
    makeAutoObservable(this, {
      store: false,
      history: false
    });
  }

  receiveProps({history}) {
    this.history = history;
  }

  setInteractiveAgent(agent) {
    this.interactiveAgent = agent;
  }

  async load() {
    await this.store._compose([
      endpoints.COMPANY_EMAIL_TEMPLATES
    ]);
    this.welcomeEmailPreview = this.store._getSingle(types.COMPANY_EMAIL_TEMPLATE, {emailType: 'survey_launch'});
  }

  async createSurvey() {
    const {model} = await this.store.post(
      endpoints.SURVEYS.SURVEYS,
      types.SURVEYS.SURVEY
    );

    this.history.push(`/manage/survey/${model.id}/edit`);
  }

  get managesSurveys() {
    return auth.hasAccess('::MANAGE_SURVEYS');
  }

  async removeSurvey(survey) {
    await this.store.destroy(survey);
  }

  openRelaunchSurveyModal(survey) {
    this.selectedSurvey = survey;
    this.relaunchSurveyModalOpen = true;
  }

  closeRelaunchSurveyModal() {
    this.relaunchSurveyModalOpen = false;
    this.selectedSurvey = null;
  }

  openCloseSurveyModal(survey) {
    this.selectedSurvey = survey;
    this.closeSurveyModalOpen = true;
  }

  closeCloseSurveyModal() {
    this.closeSurveyModalOpen = false;
    this.selectedSurvey = null;
  }

  async closeSurvey() {
    const {model} = await this.store.patch(
      endpoints.SURVEYS.SURVEY.CLOSE.with(this.selectedSurvey.id),
      types.SURVEYS.SURVEY,
      this.selectedSurvey
    );

    if (model) {
      this.interactiveAgent.refresh();
      successAlert(t('surveys.directory.close_survey.Survey closed.'));
      this.closeCloseSurveyModal();
    }
  }

  async relaunchSurvey() {
    const {model} = await this.store.patch(
      endpoints.SURVEYS.SURVEY.RELAUNCH.with(this.selectedSurvey.id),
      types.SURVEYS.SURVEY,
      this.selectedSurvey
    );

    if (model) {
      this.interactiveAgent.refresh();
      successAlert(t('surveys.directory.relaunch_survey.Survey relaunched.'));
      this.closeRelaunchSurveyModal();
    }
  }

  async duplicateSurvey(survey) {
    const {model} = await this.store.post(
      endpoints.SURVEYS.SURVEY.DUPLICATE.with(survey.id),
      types.SURVEYS.SURVEY
    );

    if (model) {
      this.history.push(`/manage/survey/${model.id}/edit`);
    }
  }

  async reopenSurvey() {
    const {model} = await this.store.patch(
      endpoints.SURVEYS.SURVEY.REOPEN.with(this.selectedSurvey.id),
      types.SURVEYS.SURVEY,
      this.selectedSurvey
    );

    if (model) {
      this.interactiveAgent.refresh();
      successAlert(t('surveys.directory.reopen_survey.Survey reopened.'));
      this.closeReopenSurveyModal();
    }
  }

  openReopenSurveyModal(survey) {
    this.selectedSurvey = survey;
    this.reopenSurveyModalOpen = true;
  }

  closeReopenSurveyModal() {
    this.selectedSurvey = null;
    this.reopenSurveyModalOpen = false;
  }

  customLinksFor(survey) {
    const links = [];
    if (survey.hasLink('relaunch')) {
      links.push({
        order: 0,
        text: 'surveys.directory.relaunch_survey.Relaunch',
        action: model => this.openRelaunchSurveyModal(model)
      });
    }
    if (survey.hasLink('duplicate')) {
      links.push({
        order: 1,
        text: 'surveys.directory.Duplicate',
        action: model => this.duplicateSurvey(model)
      });
    }
    if (survey.hasLink('close')) {
      links.push({
        order: 2,
        text: 'surveys.directory.close_survey.Close',
        action: model => this.openCloseSurveyModal(model)
      });
    }
    if (survey.hasLink('reopen')) {
      links.push({
        order: 0,
        text: 'surveys.directory.reopen_survey.Reopen',
        action: model => this.openReopenSurveyModal(model)
      });
    }

    return links;
  }

  surveyPath(survey) {
    if (survey.isLaunched) {
      return `/manage/survey/${survey.id}/`;
    } else {
      return `/manage/survey/${survey.id}/edit`;
    }
  }

  get canViewClosed() {
    return auth.hasAccess('::MANAGE_SURVEYS');
  }
}

export default SurveyDirectoryState;
